import React from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton, LinkButton, type LinkIconButtonProps } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import EmailIcon from '@atlaskit/icon/core/email';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import {
	SainAction,
	useAnalytics,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics';
import { messages } from '../../messages';
import { onKnowledgeCardLinkClick } from '../analytics';
import { onEmailCopy } from '../util';

type KnowledgeCardHeaderProps = {
	name: string;
	email?: string | null;
	profileUrl?: string;
	profileLinkText: string;
	media?: React.ReactNode;
	orgText?: React.ReactNode;
	additionalInfo?: React.ReactNode;
	onLinkClick?: (e: React.MouseEvent) => void;
	headerActions?: React.ReactNode;
	testId?: string;
};

const HeaderWrapper = cssMap({
	default: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
});

const PrimaryInfoWrapper = cssMap({
	default: {
		display: 'flex',
		width: '100%',
		height: '100%',
		flexDirection: 'column',
		justifyContent: 'center',
	},
});

const HeaderActionsWrapper = cssMap({
	default: {
		flexShrink: 0,
		marginLeft: 'auto',
	},
});

export const KnowledgeCardHeader = ({
	name,
	email,
	profileUrl,
	profileLinkText,
	media,
	orgText,
	additionalInfo,
	onLinkClick,
	headerActions,
	testId,
}: KnowledgeCardHeaderProps) => {
	const { commonAttributes } = useAIAnswerContext();
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const emailTooltip: LinkIconButtonProps['tooltip'] = {
		position: 'bottom',
		content: formatMessage(messages.knowledge_cards_copy_email_address),
		hideTooltipOnClick: true,
	};

	return (
		<Inline space="space.100" xcss={HeaderWrapper.default} testId={testId}>
			<Inline spread="space-between" alignInline="start" alignBlock="center">
				<Inline space="space.200" alignBlock="center">
					{media}
					<Stack space="space.100" xcss={PrimaryInfoWrapper.default}>
						<Heading size="small">{name}</Heading>
						{orgText && orgText}
						{additionalInfo}
					</Stack>
				</Inline>
			</Inline>
			<Inline space="space.100" xcss={HeaderActionsWrapper.default}>
				{headerActions}
				{!!email && (
					<IconButton
						icon={(props) => <EmailIcon {...props} color={token('color.icon')} />}
						label={formatMessage(messages.ai_answer_who_questions_hover_email_icon)}
						onClick={() => {
							fireAnalyticsEvent(
								onKnowledgeCardLinkClick({ ...commonAttributes, location: 'header-copy-email' }),
							);
							fireSAINUsageEvent({ metCriteria: SainAction.COPY_CLICKED });
							onEmailCopy(email);
						}}
						isTooltipDisabled={false}
						tooltip={emailTooltip}
					/>
				)}
				{profileUrl && (
					<LinkButton
						href={profileUrl}
						onClick={(e) => {
							fireAnalyticsEvent(
								onKnowledgeCardLinkClick({ ...commonAttributes, location: 'header-view-profile' }),
							);
							fireSAINUsageEvent({ metCriteria: SainAction.VIEW_PROFILE_CLICKED });
							onLinkClick?.(e);
						}}
					>
						{profileLinkText}
					</LinkButton>
				)}
			</Inline>
		</Inline>
	);
};
